import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, X, Plus, UserCircle } from "@phosphor-icons/react";

/*
  PartnerContactsMultiAutocomplete · Iter 85.76
  ─────────────────────────────────────────────
  Multi-select variant of PartnerAutocomplete for the CONTEXTUAL Partner
  field. Advisor can associate multiple people/Contacts with the same
  briefing/note. Reuses the same section layout (Partner + Firm-wide),
  the same inline "+ Add new contact" flow, and the same duplicate-
  prevention rules — the only difference is that selections stack into
  removable chips instead of replacing each other.

  Contract:
    value = {
      partner_names: string[],           // display order + free-text fallbacks
      partner_contact_ids: string[],     // canonical Contact ids (aligned to
                                         // names when linked; free-text
                                         // entries have no matching id)
    }
    onChange(value) — fires on every mutation.

    clientId narrows +Add Contact creation target. When absent, +Add is
    hidden and the picker falls back to free-text + firm-wide search.

  Backward compatibility:
    Callers passing the legacy shape { partner, partner_contact_id } are
    normalized on mount into single-item arrays so the existing composer
    state persists cleanly through the upgrade.
*/
export default function PartnerContactsMultiAutocomplete({
  value, onChange, clientId, testIdPrefix = "composer-partner",
}) {
  const partnerNames = useMemo(() => {
    if (Array.isArray(value?.partner_names)) return value.partner_names;
    if (value?.partner) return [value.partner];
    return [];
  }, [value]);
  const partnerContactIds = useMemo(() => {
    if (Array.isArray(value?.partner_contact_ids)) return value.partner_contact_ids;
    if (value?.partner_contact_id) return [value.partner_contact_id];
    return [];
  }, [value]);

  const [orgContacts, setOrgContacts] = useState([]);
  const [firmContacts, setFirmContacts] = useState([]);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!clientId) { setOrgContacts([]); return; }
    let alive = true;
    api.get(`/clients/${clientId}`)
      .then((r) => { if (alive) setOrgContacts(r.data?.stakeholders || []); })
      .catch(() => { if (alive) setOrgContacts([]); });
    return () => { alive = false; };
  }, [clientId]);

  useEffect(() => {
    let alive = true;
    api.get(`/contacts`)
      .then((r) => {
        if (!alive) return;
        const rows = r.data?.contacts || [];
        setFirmContacts(rows.map((c) => ({
          id: c.id, name: c.name, title: c.title, email: c.email,
          client_id: c.client_id, client_name: c.client_name,
        })));
      })
      .catch(() => { if (alive) setFirmContacts([]); });
    return () => { alive = false; };
  }, []);

  const selectedContactIdSet = useMemo(() => new Set(partnerContactIds), [partnerContactIds]);
  const selectedNameSet = useMemo(
    () => new Set(partnerNames.map((n) => (n || "").trim().toLowerCase()).filter(Boolean)),
    [partnerNames]
  );

  const emitChange = (nextNames, nextIds) => {
    // Keep names/ids aligned by index. Callers may still read the
    // legacy `partner` / `partner_contact_id` fields we mirror for
    // one-value-at-a-time consumers.
    onChange?.({
      partner_names: nextNames,
      partner_contact_ids: nextIds,
      partner: nextNames.join(", "),
      partner_contact_id: nextIds[0] || null,
    });
  };

  const addContact = (contact) => {
    if (selectedContactIdSet.has(contact.id)) return;
    // If the exact name already exists as free-text, upgrade THAT slot
    // to the canonical contact id instead of creating a duplicate row.
    const target = (contact.name || "").trim().toLowerCase();
    const dupIdx = partnerNames.findIndex(
      (n, i) => !partnerContactIds[i] && (n || "").trim().toLowerCase() === target
    );
    if (dupIdx >= 0) {
      const nextNames = [...partnerNames]; nextNames[dupIdx] = contact.name;
      const nextIds = [...partnerContactIds]; nextIds[dupIdx] = contact.id;
      emitChange(nextNames, nextIds);
    } else {
      emitChange([...partnerNames, contact.name], [...partnerContactIds, contact.id]);
    }
    setQuery("");
  };

  const addFreeText = (name) => {
    const trimmed = (name || query || "").trim();
    if (!trimmed) return;
    const target = trimmed.toLowerCase();
    if (selectedNameSet.has(target)) { setQuery(""); return; }
    const exact =
      orgContacts.find((c) => (c.name || "").trim().toLowerCase() === target) ||
      firmContacts.find((c) => (c.name || "").trim().toLowerCase() === target);
    if (exact) { addContact(exact); return; }
    emitChange([...partnerNames, trimmed], [...partnerContactIds, null]);
    setQuery("");
  };

  const removeAt = (idx) => {
    const nextNames = partnerNames.filter((_, i) => i !== idx);
    const nextIds = partnerContactIds.filter((_, i) => i !== idx);
    emitChange(nextNames, nextIds);
  };

  const createContact = async () => {
    const name = (query || "").trim();
    if (!name || !clientId || creating) return;
    const target = name.toLowerCase();
    const exact =
      orgContacts.find((c) => (c.name || "").trim().toLowerCase() === target) ||
      firmContacts.find((c) => (c.name || "").trim().toLowerCase() === target);
    if (exact) { addContact(exact); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/contacts`, { name });
      const contact = data?.contact;
      if (!contact?.id) throw new Error("Malformed response — no contact id returned");
      setOrgContacts((prev) => [...prev, contact]);
      addContact(contact);
      toast.success(`Added ${contact.name} to Partner contacts`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add contact");
    } finally {
      setCreating(false);
    }
  };

  // ── Suggestions filter ───────────────────────────────────────────────
  const { orgMatches, firmMatches } = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    const filterFn = (c) => {
      if (!c?.name) return false;
      if (selectedContactIdSet.has(c.id)) return false;         // hide already-picked
      if (selectedNameSet.has((c.name || "").trim().toLowerCase())) return false;
      if (!q) return true;
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.title || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q)
      );
    };
    const orgIds = new Set(orgContacts.map((c) => c.id));
    return {
      orgMatches: orgContacts.filter(filterFn).slice(0, 6),
      firmMatches: firmContacts.filter((c) => !orgIds.has(c.id)).filter(filterFn).slice(0, 6),
    };
  }, [orgContacts, firmContacts, query, selectedContactIdSet, selectedNameSet]);

  const typedTrimmed = (query || "").trim();
  const typedExact =
    typedTrimmed &&
    (
      orgContacts.some((c) => (c?.name || "").trim().toLowerCase() === typedTrimmed.toLowerCase()) ||
      firmContacts.some((c) => (c?.name || "").trim().toLowerCase() === typedTrimmed.toLowerCase())
    );
  const alreadySelected = typedTrimmed && selectedNameSet.has(typedTrimmed.toLowerCase());
  const showAddCta = typedTrimmed.length > 0 && !typedExact && !alreadySelected && !!clientId;
  const showFreeTextCta = typedTrimmed.length > 0 && !typedExact && !alreadySelected && !clientId;

  return (
    <div className="relative" data-testid={`${testIdPrefix}-multi`}>
      <div className={`flex items-center flex-wrap gap-1.5 border ${focused ? "border-primary" : "border-border"} bg-secondary/40 px-2 py-1.5 min-h-[38px]`}>
        <MagnifyingGlass size={11} className="text-muted-foreground shrink-0" />
        {partnerNames.map((name, idx) => {
          const contactId = partnerContactIds[idx];
          const linked = contactId
            ? (orgContacts.find((c) => c.id === contactId) || firmContacts.find((c) => c.id === contactId) || null)
            : null;
          return (
            <span
              key={`${contactId || "free"}-${idx}`}
              className={`inline-flex items-center gap-1.5 text-xs pl-1.5 pr-1 py-0.5 border ${linked ? "border-primary/60 bg-primary/10 text-primary" : "border-border bg-background text-foreground"}`}
              data-testid={`${testIdPrefix}-chip-${idx}`}
            >
              {linked ? <UserCircle size={11} weight="bold" /> : null}
              <span>{name}</span>
              {linked?.title && <span className="text-primary/70">· {linked.title}</span>}
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className={`ml-1 ${linked ? "text-primary/70" : "text-muted-foreground"} hover:text-destructive`}
                aria-label={`Remove ${name}`}
                data-testid={`${testIdPrefix}-chip-${idx}-remove`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          );
        })}
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 180)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && query.trim()) { e.preventDefault(); addFreeText(query); }
            if (e.key === "Backspace" && !query && partnerNames.length > 0) {
              e.preventDefault();
              removeAt(partnerNames.length - 1);
            }
            if (e.key === "Escape") { setQuery(""); setFocused(false); }
          }}
          placeholder={partnerNames.length ? "Add another…" : (clientId ? "Search contacts or type a name…" : "e.g. Rabbi Chaim Steinmetz")}
          className="flex-1 bg-transparent text-sm outline-none border-none px-0.5 py-0.5 min-w-[100px]"
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      {focused && (orgMatches.length > 0 || firmMatches.length > 0 || showAddCta || showFreeTextCta) && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 border border-border bg-card shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-dropdown`}
        >
          {orgMatches.length > 0 && (
            <div className="border-b border-border/40">
              <div className="px-3 pt-2 pb-1 text-[0.5625rem] font-mono uppercase tracking-widest text-primary">Partner</div>
              {orgMatches.map((c) => (
                <button
                  key={c.id || c.name}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addContact(c)}
                  data-testid={`${testIdPrefix}-option-${c.id}`}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/60 flex items-start justify-between gap-2 border-b border-border/40 last:border-b-0"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{c.name}</div>
                    {c.title && <div className="text-[0.6875rem] text-muted-foreground truncate">{c.title}</div>}
                    {c.email && <div className="text-[0.625rem] font-mono text-muted-foreground/80 truncate">{c.email}</div>}
                  </div>
                  <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-primary shrink-0 mt-0.5">Contact</span>
                </button>
              ))}
            </div>
          )}
          {firmMatches.length > 0 && (
            <div className="border-b border-border/40 last:border-b-0">
              <div className="px-3 pt-2 pb-1 text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground">Firm-wide</div>
              {firmMatches.map((c) => (
                <button
                  key={c.id || c.name}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => addContact(c)}
                  data-testid={`${testIdPrefix}-firm-option-${c.id}`}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/60 flex items-start justify-between gap-2 border-b border-border/40 last:border-b-0"
                >
                  <div className="min-w-0">
                    <div className="font-medium">{c.name}</div>
                    {c.title && <div className="text-[0.6875rem] text-muted-foreground truncate">{c.title}</div>}
                    <div className="text-[0.625rem] font-mono text-muted-foreground/80 truncate">
                      {c.client_name || c.email || ""}
                    </div>
                  </div>
                  <span className="text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0 mt-0.5">Firm</span>
                </button>
              ))}
            </div>
          )}
          {showAddCta && (
            <div className="p-1">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={createContact}
                disabled={creating}
                data-testid={`${testIdPrefix}-add-contact`}
                className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/60 flex items-center gap-2 disabled:opacity-60"
              >
                <Plus size={11} weight="bold" className="text-primary shrink-0" />
                <span>{creating ? "Adding…" : (<>+ Add “<span className="font-medium">{typedTrimmed}</span>” as a new contact</>)}</span>
                <span className="ml-auto text-[0.5625rem] font-mono uppercase tracking-widest text-brass">Create</span>
              </button>
            </div>
          )}
          {showFreeTextCta && (
            <div className="p-1">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addFreeText(query)}
                data-testid={`${testIdPrefix}-use-freetext`}
                className="w-full text-left px-3 py-2 text-xs hover:bg-secondary/60 flex items-center gap-2"
              >
                <Plus size={11} weight="bold" className="text-primary shrink-0" />
                <span>Add “<span className="font-medium">{typedTrimmed}</span>” as free-text partner</span>
                <span className="ml-auto text-[0.5625rem] font-mono uppercase tracking-widest text-muted-foreground">Free text</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
